import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "src/components/ui/Card";
import { Button } from "src/components/ui/Button";
import { ROUTES } from "src/routes/RouteConfig";
import { BookingStepProps } from "../types";
import { formatDateForDisplay } from "../utils/timeHelpers";
import ScrollToTop from "src/components/ScrollToTop";
import StudentParentForm from "src/components/forms/StudentParentForm";
import { supabaseServiceRole } from "src/lib/supabaseServiceClient";
import { useToast } from "src/hooks/use-toast";
import { getTutorName } from "../utils/tutorHelpers";

const BookingStepThree: React.FC<BookingStepProps> = ({
  tutor,
  formData,
  userFormData,
  onNext,
  onBack,
  onUserFormDataChange,
  isLoading,
}) => {
  const navigate = useNavigate();
  const { tutorId } = useParams();
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleStudentParentFormChange = (field: string, value: string) => {
    onUserFormDataChange({ [field]: value });
  };

  // For now, we'll use a simple validation - this should be updated based on the actual form data structure
  const isFormValid = userFormData.parentName && userFormData.studentName && userFormData.email;

  const handleConfirm = async () => {
    if (!userFormData.parentName || !userFormData.studentName || !userFormData.email) return;

    try {
      setIsSubmitting(true);

      const payload = {
        parent_name: (userFormData.parentName || '').trim(),
        student_name: (userFormData.studentName || '').trim(),
        parent_phone: (userFormData.parentPhone || '').trim(),
        email: (userFormData.email || '').trim().toLowerCase(),
        iso_country_code: (userFormData as any).isoCountryCode || null,
        phone_isd_code: (userFormData as any).phoneIsdCode || null,
        source: 'find_your_tutor',
        demo_tutor: (tutor as any)?.id ?? null,
        metadata: {
          find_your_tutor: {
            session: {
              date: formData.date ? new Date(formData.date).toISOString() : null,
              time_slot: formData.timeSlot || null,
              duration: formData.duration || null,
              topic: formData.topic || null,
              notes: formData.notes || null,
              timezone: formData.timezone || null,
            },
            tutor: {
              id: (tutor as any)?.id ?? null,
              name: getTutorName(tutor as any),
            },
          },
        },
      } as const;

      const { error } = await supabaseServiceRole
        .from('demo_requests')
        .insert(payload)
        .select('id')
        .single();

      if (error) {
        console.error('Error creating demo request from booking flow:', error);
        throw new Error(error.message || 'Failed to submit booking request');
      }

      toast({
        title: 'Request received',
        description: 'Thanks! Your booking request was submitted successfully.',
      });

      onNext();
    } catch (err: any) {
      console.error('BookingStepThree submission failed:', err);
      toast({
        title: 'Submission failed',
        description: err?.message || 'There was an error submitting your request. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ScrollToTop />
      <div className="text-center mb-8">
        <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
          Your Information
        </h1>
        <p className="mt-4 text-xl text-gray-500 max-w-2xl mx-auto">
          Please provide your details to complete the booking.
        </p>
      </div>

      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardTitle>Contact Information</CardTitle>
          <CardDescription>
            We'll use this information to send you confirmation and session details.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          
          {/* Session date/time display */}
          <div className="py-2 px-4 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-700">
              {formData.date ? formatDateForDisplay(formData.date) : "No date selected"}{" "}
              {formData.timeSlot}
              <Button
                variant="link"
                className="p-0 h-auto text-rfpurple-600 ml-2"
                onClick={() => {
                  // Go back to step 1 to edit date/time
                  onBack();
                  onBack(); // Go back twice to reach step 1
                }}
              >
                Edit
              </Button>
            </p>
          </div>

          {/* Student Parent Form */}
          <StudentParentForm
            formData={{
              parentName: userFormData.parentName || "",
              studentName: userFormData.studentName || "",
              parentPhone: userFormData.parentPhone || "",
              email: userFormData.email || "",
              isoCountryCode: (userFormData as any).isoCountryCode || "",
              phoneIsdCode: (userFormData as any).phoneIsdCode || "",
            }}
            formErrors={{}} // You may want to add error handling here
            onInputChange={handleStudentParentFormChange}
            showGradeSelection={true}
            showPrimaryGoals={false}
          />

          {/* Login option */}
          <div className="pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-600">
              Already have an account?
              <Button
                variant="link"
                className="p-0 h-auto text-rfpurple-600 ml-1"
                onClick={() =>
                  navigate(ROUTES.LOGIN.path, {
                    state: {
                      returnTo: `/book-session/${tutorId}?step=3`,
                      bookingData: {
                        tutorId,
                        date: formData.date,
                        timeSlot: formData.timeSlot,
                        duration: formData.duration,
                        topic: formData.topic,
                        notes: formData.notes,
                      },
                    },
                  })
                }
              >
                Log in
              </Button>{" "}
              to pre-fill your information.
            </p>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button
            className="button-gradient text-white"
            onClick={handleConfirm}
            disabled={!isFormValid || isLoading || isSubmitting}
          >
            {isLoading || isSubmitting ? "Processing..." : "Confirm Booking"}
          </Button>
        </CardFooter>
      </Card>
    </>
  );
};

export default BookingStepThree;